import React from 'react';
import {Image, ImageSourcePropType, StyleSheet, View} from 'react-native';

import Spacer from '../../../components/rewards/Spacer/Spacer';
import Button from '../../../components/ui-controls/Button';
import PressableWithOpacityFeedback from '../../../components/ui-controls/PressableWithOpacityFeedback';
import Text from '../../../components/ui-controls/Text';
import FormField from '../../../design-system/form/FormField';
import {lineHeight, type} from '../../../design-system/tokens';
import heroImg from '../../../images/rewards/signup-hero.jpg';
import heroWideImg from '../../../images/rewards/signup-hero-wide.jpg';
import CloseImg from '../../../images/svg/icons/SignUpClose.svg';
import theme from '../../../style/theme';
import {emailSchema} from '../../account/schemas/authSchemas';

import SignUpPageLayout, {CTA_HEIGHT, CTA_RADIUS, useWideLayout} from './SignUpPageLayout';

export const DEFAULT_HEADING = "LET'S GET STARTED.";
export const DEFAULT_BODY =
  "Pop your email address below. We'll check if there's an existing account, or, we can start a new one for you.";

export interface SignUpEmailScreenContentProps {
  /** Current value of the email field. */
  email: string;
  /** Called on every keystroke in the email field. */
  onChangeEmail: (email: string) => void;
  /** Called when the enabled NEXT button is pressed. */
  onNext: () => void;
  /** Called when the close icon is pressed. */
  onClose: () => void;
  /** Shown against the email field, e.g. when the account lookup fails. */
  errorMessage?: string;
  /** Overrides the bundled hero banner for the layout in play. */
  heroImage?: ImageSourcePropType;
  heading?: string;
  body?: string;
}

/**
 * Presentational content for sign-up Screen 1 (email entry).
 * Props in, callbacks out: no navigation, no store access, no API calls.
 *
 * The brand header is not rendered here — the route supplies it. Below it the
 * narrow layout adds a close icon; the wide frame has none.
 *
 * The layout switches on the width this component is actually given rather than
 * on the window width, so it renders correctly inside a narrow Storybook frame
 * on a desktop browser as well as in the app. SignUpPageLayout owns that shell;
 * `useWideLayout` owns the measurement.
 */
const SignUpEmailScreenContent = ({
  email,
  onChangeEmail,
  onNext,
  onClose,
  errorMessage,
  heroImage,
  heading = DEFAULT_HEADING,
  body = DEFAULT_BODY,
}: SignUpEmailScreenContentProps) => {
  const {isDesktopCard: isWide, onLayout} = useWideLayout();
  const isEmailValid = emailSchema.safeParse(email).success;

  const hero = (
    <View style={[styles.hero, isWide && styles.heroWide]}>
      <Image
        source={heroImage ?? (isWide ? heroWideImg : heroImg)}
        style={styles.heroImage}
        resizeMode="cover"
      />
    </View>
  );

  const above = (
    <>
      <Spacer size={16} />

      {/* Narrow layout only: the wide frame has no close icon, because the
          header's top navigation is the way out on a desktop browser. */}
      <View style={styles.closeRow}>
        <PressableWithOpacityFeedback
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close"
          testID="signUpEmailClose">
          {/* Decorative: the Pressable carries the role and the label. Figma
            draws the 16px cross centred in the 24px hit box. */}
          <View style={styles.closeIcon}>
            <CloseImg
              width={CLOSE_GLYPH_SIZE}
              height={CLOSE_GLYPH_SIZE}
              color={theme.colour.text.link.primary}
              aria-hidden={true}
            />
          </View>
        </PressableWithOpacityFeedback>
      </View>

      <Spacer size={16} />

      {hero}

      <Spacer size={24} />

      <View style={styles.paragraphBlock}>
        {/* SHORTCUT: `variant="heading1"` (theme) is grandfathered. The token
            equivalent, `type.heading.h1` in app/design-system/tokens, exists
            since DIS-1736 merged. Migration is deferred to avoid a visual
            change - DIS-1735 is the epic; no mass migration. */}
        <Text variant="heading1" align="center">
          {heading}
        </Text>
        <Text style={[type.paragraph.p2, lineHeight.paragraph.p2]} align="center">
          {body}
        </Text>
      </View>

      <Spacer size={40} />
    </>
  );

  const field = (
    <FormField
      label="Email"
      required
      error={!!errorMessage}
      errorMessage={errorMessage}
      value={email}
      onChangeText={onChangeEmail}
      placeholder="your@email.com"
      keyboardType="email-address"
      autoComplete="email"
      autoCapitalize="none"
      testID="signUpEmail"
    />
  );

  const cta = (
    <Button
      title="NEXT"
      onPress={onNext}
      disabled={!isEmailValid}
      buttonStyle={styles.cta}
      disabledStyle={styles.ctaDisabled}
      titleStyle={styles.ctaTitle}
      disabledTitleStyle={styles.ctaTitleDisabled}
      testID="signUpEmailNext"
    />
  );

  // Desktop (155:6410): the hero banner, then the centred card, as on the Lite
  // Welcome screen. No `above`: the centred branch renders it too, which would
  // draw the hero twice.
  if (isWide) {
    return (
      <SignUpPageLayout
        centred
        isWide={isWide}
        onLayout={onLayout}
        testID="signUpEmailScreen"
        banner={hero}
        heading={
          <Text style={[type.heading.h1, lineHeight.heading.h1, styles.headingWide]}>
            {heading}
          </Text>
        }
        // Desktop body is P1; mobile keeps P2 as drawn.
        intro={
          <Text style={[type.paragraph.p1, lineHeight.paragraph.p1, styles.centred]}>{body}</Text>
        }
        main={field}
        aside={cta}
      />
    );
  }

  return (
    <SignUpPageLayout
      isWide={isWide}
      onLayout={onLayout}
      testID="signUpEmailScreen"
      above={above}
      main={field}
      aside={cta}
      asideAlign="bottom"
      stackedGap={24}
    />
  );
};

export default SignUpEmailScreenContent;

// SHORTCUT: spacing literals - CLOSE_ICON_SIZE, CLOSE_GLYPH_SIZE, PARAGRAPH_GAP, the Spacer sizes
// 16 / 24 / 40 and stackedGap 24 in the render above. Spacer sizes are limited
// to its SpacerSize union (16 | 24 | 40 | 64). Ceiling: this screen. Upgrade
// path: a spacing module under app/design-system/tokens/ - DIS-1737 blocks it,
// DIS-1735 is the epic. HERO_ASPECT and HERO_ASPECT_WIDE are the supplied image
// crops, not design-system values.
const CLOSE_ICON_SIZE = 24;
const CLOSE_GLYPH_SIZE = 16; // Figma cross, centred in CLOSE_ICON_SIZE
// The two supplied hero crops. Height follows the width through `aspectRatio`
// so the banner never letterboxes at any screen size.
const HERO_ASPECT = 1372 / 540;
const HERO_ASPECT_WIDE = 2000 / 279;
const PARAGRAPH_GAP = 16;

const styles = StyleSheet.create({
  closeRow: {
    alignItems: 'flex-end',
  },
  closeIcon: {
    width: CLOSE_ICON_SIZE,
    height: CLOSE_ICON_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hero: {
    width: '100%',
    aspectRatio: HERO_ASPECT,
    overflow: 'hidden',
    backgroundColor: theme.colour.border.tile,
  },
  heroWide: {
    aspectRatio: HERO_ASPECT_WIDE,
  },
  heroImage: {
    width: '100%',
    height: '100%',
  },
  paragraphBlock: {
    gap: PARAGRAPH_GAP,
  },
  // Wide only: the centred card spaces the heading and intro itself.
  headingWide: {
    color: theme.colour.text.heading.primary,
    textAlign: 'center',
  },
  centred: {
    textAlign: 'center',
  },
  cta: {
    width: '100%',
    height: CTA_HEIGHT,
    borderRadius: CTA_RADIUS,
    backgroundColor: theme.colour.background.button.primary,
  },
  ctaDisabled: {
    backgroundColor: theme.colour.background.button.disabled,
  },
  // @rneui's Button sets no font family, so without one the title renders in the
  // platform system stack instead of the brand face. The token carries family and
  // weight together through `resolveWeight`, the only boundary that gets bold
  // right on Android.
  ctaTitle: {
    ...type.button.primary,
    color: theme.colour.background.button.contrastOnPrimary,
  },
  // The theme's own disabled-label colour, used because white on the disabled
  // grey measures 1.50:1. Measured 2026-09-22: #666666 on 'lightgrey' (#D3D3D3)
  // is 3.84:1, identical across all four brands. That clears WCAG AA's 3:1 for
  // large text but not the 4.5:1 this title actually needs — bold under 18.66px
  // is normal text, so neither the previous 16px nor this 14px qualifies for the
  // large-text threshold. Pre-existing shortfall, unchanged by the token swap;
  // fixing it means darkening the theme's disabled pair on every brand.
  ctaTitleDisabled: {
    color: theme.colour.text.button.disabled,
  },
});
